import * as React from "react";

/**
 * Returns positioning values for a bottom sheet that:
 * - Never overlaps the status bar (uses safe-area-inset-top + topGap)
 * - Sits above the keyboard when it's open
 * - Content inside scrolls to keep focused inputs visible
 */
export function useBottomSheetViewport(topGap = 12) {
  const [values, setValues] = React.useState({
    bottomPx: 0,
    availableHeight: 0,   // 0 means "use CSS fallback"
  });

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    // Read the CSS env safe area top; fallback ~47px for iOS notch
    const getSafeTop = () => {
      const el = document.documentElement;
      const raw = getComputedStyle(el).getPropertyValue("--sat");
      const parsed = parseFloat(raw);
      return parsed > 0 ? parsed : 0;
    };

    // Set CSS custom property for safe-area-inset-top so we can read it
    document.documentElement.style.setProperty(
      "--sat",
      "env(safe-area-inset-top, 0px)"
    );

    const update = () => {
      const vp = window.visualViewport;
      const safeTop = getSafeTop() || 0;
      const reservedTop = safeTop + topGap;

      if (!vp) {
        setValues({
          bottomPx: 0,
          availableHeight: window.innerHeight - reservedTop,
        });
        return;
      }

      const keyboardInset = Math.max(
        0,
        window.innerHeight - vp.height - vp.offsetTop
      );
      const availableHeight = Math.max(
        280,
        window.innerHeight - keyboardInset - reservedTop
      );

      setValues({ bottomPx: keyboardInset, availableHeight });
    };

    // Small delay for env() to resolve
    requestAnimationFrame(update);

    const vp = window.visualViewport;
    vp?.addEventListener("resize", update);
    vp?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);

    return () => {
      vp?.removeEventListener("resize", update);
      vp?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, [topGap]);

  return values;
}
